import {
  DetailedHTMLProps,
  forwardRef,
  LegacyRef,
  SelectHTMLAttributes,
} from "react";

import { DropdownSelectSchemaField } from "types/schema";

import styles from "./DropdownSelect.module.scss";

type RefinedInputProps = Omit<
  DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
  "value"
>;

type DropdownOptions = Record<string, string | number>;

type DropdownSelectProps = RefinedInputProps & {
  label?: string;
  options: DropdownOptions | DropdownSelectSchemaField["allow"];
  placeholder?: string;
};

/** Tells us whether these options are plain strings or key:value pairs */
function isObjectOptions(
  options: DropdownSelectProps["options"]
): options is DropdownOptions {
  return !Array.isArray(options);
}

/**
 * Dropdown for select fields.
 *
 * Uses a select element and iterates through the options props
 */
const DropdownSelect = forwardRef(
  (
    { className, options, placeholder, ...selectProps }: DropdownSelectProps,
    ref: LegacyRef<HTMLSelectElement> | undefined
  ) => {
    if (isObjectOptions(options)) {
      return (
        <select
          ref={ref}
          className={[styles.dropdown, className].join(" ")}
          {...selectProps}
        >
          <option>{placeholder ?? ""}</option>

          {Object.entries(options).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <select
        ref={ref}
        className={[styles.dropdown, className].join(" ")}
        {...selectProps}
      >
        <option>{placeholder ?? ""}</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
);

export default DropdownSelect;
